import type { FirmRole } from "@contract/firm/me";
import type { InviteOutcome } from "@contract/firm/members";
import { Button, Group, Modal, Select, Stack, Text, Textarea } from "@mantine/core";
import * as React from "react";
import { track } from "../../analytics/analytics";
import { ApiError } from "../../api/client";
import type * as membersApi from "../../api/members";
import { parseEmailList } from "../../api/members";
import { SecondaryButton } from "../../ui/buttons";
import { Notice } from "../../ui/Notice";
import { PANEL_PALETTES } from "../../ui/tokens";
import { ROLE_LABEL } from "../membersModel";

/** The invite modal (spec §10.2): one or more pasted emails and a role, the seats it uses,
 * and each address's outcome inline. */
export function InviteModal({
  opened,
  onClose,
  onInvited,
  invite,
  seatsLeft,
}: {
  opened: boolean;
  onClose: () => void;
  onInvited: () => void;
  invite: (typeof membersApi)["inviteMembers"];
  seatsLeft: number | null;
}) {
  const [text, setText] = React.useState("");
  const [role, setRole] = React.useState<FirmRole>("member");
  const [busy, setBusy] = React.useState(false);
  const [results, setResults] = React.useState<InviteOutcome[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const emails = parseEmailList(text);

  const close = () => {
    setText("");
    setResults(null);
    setError(null);
    onClose();
  };

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = await invite(emails, role);
      setResults(out);
      track("web_invites_sent", { sent: out.filter((r) => r.ok).length, refused: out.filter((r) => !r.ok).length });
      onInvited();
      if (out.every((r) => r.ok)) close();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "We couldn't send the invites. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="Invite Team Members" size="md">
      <Stack gap="md">
        <Textarea
          label="Emails"
          description="Paste one or more addresses. Each gets its own invite, valid for 14 days."
          rows={4}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
        />
        {seatsLeft !== null ? (
          <Text c={emails.length > seatsLeft ? PANEL_PALETTES.red[7] : "dimmed"}>Uses 1 seat per invite. {seatsLeft} left.</Text>
        ) : null}
        <Select
          label="Role"
          data={[
            { value: "member", label: ROLE_LABEL.member },
            { value: "admin", label: ROLE_LABEL.admin },
          ]}
          value={role}
          onChange={(v) => v && setRole(v as FirmRole)}
          allowDeselect={false}
        />
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {results ? (
          <Stack gap={4}>
            {results.map((r) => (
              <Text key={r.email} c={r.ok ? PANEL_PALETTES.green[8] : PANEL_PALETTES.red[7]}>
                {r.email}: {r.ok ? "invite sent" : r.error}
              </Text>
            ))}
          </Stack>
        ) : null}
        <Group justify="flex-end" gap={5}>
          <SecondaryButton onClick={close}>
            {results ? "Done" : "Cancel"}
          </SecondaryButton>
          <Button loading={busy} disabled={emails.length === 0} onClick={() => void send()}>
            Send {emails.length > 1 ? `${emails.length} invites` : "invite"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
